import { env } from '@cc-shopping/platform-env';
import type { ImageLoaderProps } from 'next/image';

const CDN_IMAGE_PREFIX = '/cdn-cgi/image/';

/**
 * Custom image loader for Next.js that routes image optimization through Cloudflare CDN
 *
 * This loader bypasses Vercel's image optimization service and uses Cloudflare's
 * /cdn-cgi/image/ endpoint for all image transformations, avoiding additional costs
 * while maintaining optimal performance.
 *
 * Handles:
 * - Absolute NerdWallet URLs: https://www.nerdwallet.com/...
 * - Relative URLs: /cdn-cgi/image/..., /tachyon/..., /cdn/...
 * - Images already in CDN format: rebuilds with new parameters
 * - External domains: returned unchanged (no optimization)
 *
 * Next.js handles srcset generation with different widths for different DPRs,
 * so we don't need to apply DPR multiplication here - just pass the width directly.
 *
 * @param src - The source URL of the image (absolute or relative)
 * @param width - The desired width for the image (already accounts for DPR in srcset)
 * @param quality - The desired quality (1-100), defaults to 80
 * @returns Optimized image URL with Cloudflare CDN parameters
 */
export default function cloudflareImageLoader({ src, width, quality }: ImageLoaderProps): string {
  // Handle relative URLs by prepending NerdWallet domain
  const absoluteSrc = src.startsWith('/')
    ? `${env.NEXT_PUBLIC_NW_URL.replace(/\/$/, '')}${src}`
    : src;

  try {
    const url = new URL(absoluteSrc);

    // Only optimize images from NerdWallet domains
    if (!url.hostname.includes('nerdwallet.com') && !url.hostname.includes('nerdwallet.biz')) {
      return src;
    }

    // Build Cloudflare transformation options
    const options = [`format=auto`, `width=${width}`, `quality=${quality || 80}`, `sharpen=1`];

    // If already has CDN prefix, extract the image path and rebuild with new options
    if (absoluteSrc.includes(CDN_IMAGE_PREFIX)) {
      const cdnPrefixIndex = absoluteSrc.indexOf(CDN_IMAGE_PREFIX);
      const baseUrl = absoluteSrc.substring(0, cdnPrefixIndex);
      const afterPrefix = absoluteSrc.substring(cdnPrefixIndex + CDN_IMAGE_PREFIX.length);
      const pathStartIndex = afterPrefix.indexOf('/');
      const imagePath =
        pathStartIndex >= 0 ? afterPrefix.substring(pathStartIndex + 1) : afterPrefix;

      // Edge case: if imagePath looks like old options (contains '=' but no '/'), it means there's no actual path
      // This would create a malformed URL like "/cdn-cgi/image/options/old-options", so return original
      if (imagePath.includes('=') && !imagePath.includes('/')) {
        return src;
      }

      return `${baseUrl}${CDN_IMAGE_PREFIX}${options.join(',')}/${imagePath}`;
    }

    // For non-CDN images (Tachyon, etc.), wrap with cdn-cgi prefix
    const imagePath = url.pathname + url.search;
    return `${url.origin}${CDN_IMAGE_PREFIX}${options.join(',')}${imagePath}`;
  } catch (_e) {
    // If URL parsing fails, return original
    return src;
  }
}
